import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '@/contexts/FirebaseAuthContext';
import { useCurrency } from '@/contexts/CurrencyContext';
import { db } from '@/lib/firebase';
import { collection, getDocs } from 'firebase/firestore';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/useToast';
import { ShoppingBag, Gift, Loader2 } from 'lucide-react';
import FreeItemModal from '@/features/store/components/FreeItemModal';
import StoreCheckoutModal from '@/features/store/components/StoreCheckoutModal';
import { initializeStoreProducts } from '@/lib/initializeStoreProducts';
import { useMobile } from '@/features/shared/contexts/MobileContext';
import { ysr2Mobile } from '@/features/shared/styles/mobileDesignSystem';
import StoreProductImageCarousel from '@/features/store/components/StoreProductImageCarousel';

const StorePage = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const { format } = useCurrency();
  const mobile = useMobile();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showFreeModal, setShowFreeModal] = useState(false);
  const [checkoutProduct, setCheckoutProduct] = useState(null);

  useEffect(() => {
    const initializeAndLoad = async () => {
      try {
        setLoading(true);
        // Initialize products if they don't exist
        if (db) {
          const result = await initializeStoreProducts();
          if (result.success) {
            console.log('Products initialized:', result);
          }
        }
        // Load products
        await loadProducts();
      } catch (error) {
        console.error('Error initializing or loading products:', error);
        // Still try to load products even if initialization fails
        await loadProducts();
      }
    };
    initializeAndLoad();
  }, []);

  const loadProducts = async () => {
    try {
      setLoading(true);
      const productsRef = collection(db, 'store_products');
      const productsSnapshot = await getDocs(productsRef);
      const productsData = productsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Sort by order if available
      productsData.sort((a, b) => (a.order || 0) - (b.order || 0));
      setProducts(productsData);
    } catch (error) {
      console.error('Error loading products:', error);
      toast({
        title: 'Error',
        description: 'Failed to load store products.',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleBuy = (product) => {
    setCheckoutProduct(product);
  };

  const handleGetFree = (product) => {
    setSelectedProduct(product);
    setShowFreeModal(true);
  };

  if (loading) {
    return (
      <div className={`${mobile.isMobile ? ysr2Mobile.shell : 'min-h-screen bg-black'} flex items-center justify-center pb-20`}>
        <Loader2 className="w-8 h-8 animate-spin text-blue-400" />
      </div>
    );
  }

  return (
    <div className={`${mobile.isMobile ? ysr2Mobile.shell : 'min-h-screen bg-black'} text-white pb-20`}>
      {mobile.isMobile && <div className={ysr2Mobile.orbBlue} />}
      {mobile.isMobile && <div className={ysr2Mobile.orbPurple} />}
      <div className={`${mobile.isMobile ? 'relative z-10 px-4 pt-4 pb-28' : 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8'}`}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className={`${mobile.isMobile ? `${ysr2Mobile.surfaceStrong} p-4` : ''} mb-8`}
        >
          <div className="flex items-center space-x-3 mb-2">
            <ShoppingBag className="w-8 h-8 text-blue-200" />
            <h1 className="text-3xl font-bold">Store</h1>
          </div>
          <p className="text-blue-100/70">Get merchandise and promotional items for your performances</p>
        </motion.div>

        {products.length === 0 ? (
          <Card className={mobile.isMobile ? ysr2Mobile.surface : 'bg-gray-800 border-gray-700'}>
            <CardContent className="p-8 text-center">
              <ShoppingBag className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400">No products available at the moment.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {products.map((product) => (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3 }}
              >
                <Card className={`${mobile.isMobile ? ysr2Mobile.surface : 'bg-gray-800 border-gray-700'} hover:border-blue-300/40 transition-colors h-full flex flex-col`}>
                  <StoreProductImageCarousel product={product} alt={product.name} heightClass="h-48" />
                  <CardHeader>
                    <CardTitle className="text-xl">{product.name}</CardTitle>
                    <CardDescription className="text-gray-400">
                      {product.description || 'No description available'}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="flex-1 flex flex-col justify-between">
                    <div className="mb-4">
                      <p className="text-2xl font-bold text-blue-200">
                        {format(parseFloat(product.price) || 0)}
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Button
                        onClick={() => handleBuy(product)}
                        className="w-full bg-blue-600 hover:bg-blue-500"
                      >
                        <ShoppingBag className="w-4 h-4 mr-2" />
                        Buy Now
                      </Button>
                      <Button
                        onClick={() => handleGetFree(product)}
                        variant="outline"
                        className="w-full border-green-500 text-green-400 hover:bg-green-500 hover:text-white text-sm"
                      >
                        <Gift className="w-3 h-3 mr-1" />
                        Get &quot;{product.name}&quot; for FREE!
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {checkoutProduct && (
        <StoreCheckoutModal
          product={checkoutProduct}
          isOpen={!!checkoutProduct}
          onClose={() => setCheckoutProduct(null)}
          surfaceClass={mobile.isMobile ? ysr2Mobile.surface : 'bg-gray-800'}
        />
      )}

      {showFreeModal && selectedProduct && (
        <FreeItemModal
          product={selectedProduct}
          isOpen={showFreeModal}
          onClose={() => {
            setShowFreeModal(false);
            setSelectedProduct(null);
          }}
        />
      )}
    </div>
  );
};

export default StorePage;
